import {useLazyQuery, useSuspenseQuery} from "@apollo/client";
import {captureException} from "@sentry/react";
import get from "lodash/get";
import {useState} from "react";

import {
  AlertExtraDataResponse,
  CheckGlobalAlertResponse,
  GQL_ALERT_EXTRA_DATA,
  GQL_CHECK_GLOBAL_ALERT,
} from "@/lib/queries/alerting.ts";
import {AlertExtraData} from "@/lib/types/alerting.ts";
import {CheckStepDef, MonitoringServiceType} from "@/lib/types/checks.ts";
import {DownAlertsList} from "@/pages/alerting/components/details/DownAlertsList.tsx";
import {NetworkAlertDetails} from "@/pages/alerting/components/details/NetworkAlertDetails.tsx";
import {RootCauseAnalysis} from "@/pages/alerting/components/details/RootCauseAnalysis.tsx";

enum AlertDetailsView {
  DOWN_ALERTS_LIST = "DOWN_ALERTS_LIST",
  ROOT_CAUSE_ANALYSIS = "ROOT_CAUSE_ANALYSIS",
}

export type AlertDetailsProps = {
  id: number;
};

export const AlertDetails = ({id}: AlertDetailsProps) => {
  const [extraAlertData, setExtraAlertData] = useState<AlertExtraData | null>(null);
  const [view, setView] = useState<AlertDetailsView>(AlertDetailsView.DOWN_ALERTS_LIST);
  const {
    data: {globalAlertState, stepsDef},
  } = useSuspenseQuery<CheckGlobalAlertResponse>(GQL_CHECK_GLOBAL_ALERT, {
    variables: {
      id: id,
    },
    fetchPolicy: "no-cache",
  });
  const [loadExtraData] = useLazyQuery<AlertExtraDataResponse>(GQL_ALERT_EXTRA_DATA);

  const checkType = globalAlertState.service_monitoring_type;
  const isNetwork = checkType === MonitoringServiceType.NETWORK;
  const withAlertExtraData = [MonitoringServiceType.TRANSACTION, MonitoringServiceType.API].includes(checkType);

  const onAlertDataRequest = async (id: number) => {
    const {data, error} = await loadExtraData({variables: {id}});
    if (error) {
      captureException(error);
    }
    if (data?.alertData) {
      setExtraAlertData(data?.alertData);
      setView(AlertDetailsView.ROOT_CAUSE_ANALYSIS);
    }
  };

  // NETWORK (Global Ping) alerts skip the down-alerts list entirely and open
  // straight into the structured probe view, loaded from the representative alert
  // (which carries the gphttp ext_data). `alert` can be null on an alert-less
  // state, so fall back to a down-alerts entry, then to a no-details message.
  if (isNetwork) {
    const networkAlert = globalAlertState.alert ?? globalAlertState.down_alerts[0] ?? null;
    // NetworkAlertDetails applies its own full-bleed wrapper around the two-pane
    // probe view (and keeps normal padding on the error/no-details fallbacks), so
    // no wrapper inset here.
    return networkAlert ? (
      <NetworkAlertDetails alertId={networkAlert.id} />
    ) : (
      <p className="text-muted p-3 mb-0">No details were recorded for this alert.</p>
    );
  }

  const checkStepsDef: CheckStepDef[] | undefined = get(stepsDef, checkType);
  return (
    <div className="p-1">
      {globalAlertState && view === AlertDetailsView.DOWN_ALERTS_LIST && (
        <DownAlertsList
          gas={globalAlertState}
          onAlertDataRequest={withAlertExtraData ? onAlertDataRequest : undefined}
        />
      )}
      {checkStepsDef && extraAlertData && view === AlertDetailsView.ROOT_CAUSE_ANALYSIS && (
        <RootCauseAnalysis
          onGoBack={() => {
            setView(AlertDetailsView.DOWN_ALERTS_LIST);
          }}
          alertData={extraAlertData}
          stepsDef={checkStepsDef}
        />
      )}
    </div>
  );
};
